/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { Dispatch, SetStateAction } from 'react';
import {
    EeCheckApiError,
    getOriginalEeCheckProxyRequest,
} from '@/api/eeCheckApi';
import type { DebugRequestEntry } from '@/types/ui';

export function toDebugString(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value === 'string') {
        return value;
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

export function createResponsePreview(value: string): string {
    return value.length > 100 ? `${value.slice(0, 100)}...` : value;
}

export function createRawResponsePreview(data: unknown, fallbackRawResponse: string): string {
    if (typeof data === 'string') {
        return createResponsePreview(data);
    }

    if (data !== undefined) {
        return createResponsePreview(toDebugString(data));
    }

    return createResponsePreview(fallbackRawResponse);
}

export function appendDebugEntry(
    setDebugEntries: Dispatch<SetStateAction<DebugRequestEntry[]>>,
    url: string,
    parameters: unknown,
    otherParameters: unknown,
    success: boolean,
    responsePreview: string,
    durationMs: number | null,
) {
    const originalRequest = getOriginalEeCheckProxyRequest(url, parameters);

    setDebugEntries((currentEntries) => [
        ...currentEntries,
        {
            durationMs,
            id: currentEntries.length + 1,
            ...(originalRequest ? { originalRequest } : {}),
            otherParameters,
            parameters,
            requestedAt: new Date().toISOString(),
            responsePreview,
            success,
            url,
        },
    ]);
}

export function isExpiredSessionError(error: unknown): error is EeCheckApiError {
    return error instanceof EeCheckApiError && error.status === 401;
}
